import React from 'react';
import { useQueryClient } from 'react-query';
import { Account, IFormulaToAccount } from '../../../sp-types/accountTypes';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { IExposureHistory } from '../../../utils/exposureUtils';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { HedgesDerivedMaterialsSection } from './HedgesDerivedMaterialsSection';
import { Save, Refresh } from '@mui/icons-material';
import { CircularProgress, Tooltip as MuiTooltip } from '@mui/material';
import { triggerExposureCalculation, ExposureSnapshot } from '../../../services/exposureService';
import { WatchRuleSettingsPanel } from './WatchRuleSettingsPanel';
import type { WatchRule, RuleTypeMeta } from '../../../hooks/useWatchRules';

interface ExposureStats {
  safeZonePercentage: number;
  longestOutsideSafeZoneDays: number;
  warningZoneEntries: number;
  totalWarningZoneDays: number;
  criticalZoneEntries: number;
  totalCriticalZoneDays: number;
  totalDays: number;
}

interface Props {
  formulaId: string;
  startDate: Date;
  colorsMap: Record<string, string>;
  account: Account;
  exposureHistory?: IExposureHistory[];
  accountMaterial?: IFormulaToAccount;
  activeSub?: string;
  accountId?: string;
  selectedMonthIndex?: number;
  latestSnapshot?: ExposureSnapshot;
  onRecalculated?: () => void;
  headerAction?: { save: () => Promise<void>; isSaving: boolean; canSave: boolean };
  watchRule?: WatchRule;
  watchRuleMeta?: RuleTypeMeta;
  allWatchRules?: WatchRule[];
  allRuleTypes?: RuleTypeMeta[];
  onWatchRuleSave?: (ruleId: number, condition: Record<string, any>) => Promise<void>;
  isWatchRuleSaving?: boolean;
  onCreateRule?: (ruleType?: string) => void;
  isCreatingRule?: boolean;
}
const toMonthStr = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export const StrategyDetails: React.FC<Props> = ({
  formulaId,
  startDate,
  colorsMap: _colorsMap,
  account: _account,
  exposureHistory,
  accountMaterial,
  activeSub,
  accountId,
  latestSnapshot,
  onRecalculated,
  headerAction,
  selectedMonthIndex,
  watchRule,
  watchRuleMeta,
  allWatchRules,
  allRuleTypes,
  onWatchRuleSave,
  isWatchRuleSaving,
  onCreateRule,
  isCreatingRule,
}) => {
  const { pricesMap } = useCurrencyRates();
  const queryClient = useQueryClient();
  const [isCalculating, setIsCalculating] = React.useState(false);
  const [calcResult, setCalcResult] = React.useState<string | null>(null);

  const handleCalculateNow = React.useCallback(async () => {
    if (!accountId) return;
    setIsCalculating(true);
    setCalcResult(null);
    try {
      const numericAccountId = typeof accountId === 'string' ? parseInt(accountId) : accountId;
      const result = await triggerExposureCalculation(numericAccountId);
      setCalcResult(`✓ ${result.formulaCount} formulas calculated`);
      queryClient.invalidateQueries(['exposureHistory']);
      queryClient.invalidateQueries(['latestExposure']);
      onRecalculated?.();
    } catch (err) {
      console.error('[Exposure] Calculate failed:', err);
      setCalcResult('✗ Calculation failed');
    } finally {
      setIsCalculating(false);
    }
  }, [accountId, queryClient]);

  // --- Exposure statistics (shared across all policies) ---
  const calculateExposureValue = React.useCallback(
    (exposure: IExposureHistory): number => {
      const rawExposure = exposure.futureOrders - exposure.inventory - exposure.financialPosition;
      const priceMultiplier = formulaId === GENERAL_ID ? 1 : pricesMap[formulaId] || 1;
      return rawExposure * priceMultiplier;
    },
    [formulaId, pricesMap],
  );

  // Get risk tolerance ranges — prefer watch rule, fall back to accountMaterial
  const riskRanges = React.useMemo(() => {
    const divide = formulaId === GENERAL_ID ? 1 : pricesMap[formulaId] || 1;

    // Watch rule is the single source of truth for exposure thresholds
    const cond = watchRule?.rule_type === 'exposure_threshold' ? watchRule.condition : null;
    if (cond && (cond.danger_below || cond.warn_below || cond.warn_above || cond.danger_above)) {
      return {
        criticalLow: (Number(cond.danger_below) || 0) / divide,
        warningLow: (Number(cond.warn_below) || 0) / divide,
        safeStart: (Number(cond.warn_below) || 0) / divide,
        warningHigh: (Number(cond.warn_above) || 0) / divide,
        criticalHigh: (Number(cond.danger_above) || 0) / divide,
      };
    }

    // Fallback to accountMaterial until all accounts have watch rules
    if (!accountMaterial) return null;
    const { RiskTolerance } = accountMaterial;
    return {
      criticalLow: RiskTolerance.dangerMinus / divide,
      warningLow: RiskTolerance.warnMinus / divide,
      safeStart: RiskTolerance.warnMinus / divide,
      warningHigh: RiskTolerance.warnPlus / divide,
      criticalHigh: RiskTolerance.dangerPlus / divide,
    };
  }, [watchRule, accountMaterial, formulaId, pricesMap]);

  // Calculate exposure statistics with proper date differences
  const exposureStats = React.useMemo((): ExposureStats | null => {
    if (!exposureHistory || !riskRanges || exposureHistory.length === 0) {
      return null;
    }

    // Assume exposureHistory is already sorted by date
    if (exposureHistory.length < 2) {
      // Not enough data points to calculate meaningful statistics
      return null;
    }

    let safeZoneDays = 0;
    let warningZoneEntries = 0;
    let totalWarningZoneDays = 0;
    let criticalZoneEntries = 0;
    let totalCriticalZoneDays = 0;
    let longestOutsideSafeZoneDays = 0;

    let previousZone: 'safe' | 'warning' | 'critical' | null = null;
    let currentOutsideSafeStart: Date | null = null;

    // Helper function to calculate days between two dates
    const getDaysDiff = (date1: Date, date2: Date): number => {
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      return Math.ceil(diffTime / (3_600_000 * 24));
    };

    for (let i = 0; i < exposureHistory.length; i++) {
      const currentExposure = exposureHistory[i];
      const currentDate = currentExposure.createDate;
      const nextExposure = exposureHistory[i + 1]; // This is safe because we check i < length

      // Calculate days this exposure state lasted
      let daysInCurrentState: number;
      if (nextExposure) {
        const nextDate = nextExposure.createDate;
        daysInCurrentState = getDaysDiff(currentDate, nextDate);
      } else {
        // For the last data point, assume it lasted until today or use minimum of 1 day
        daysInCurrentState = 1; //Math.max(1, getDaysDiff(currentDate, today));
      }

      const exposureValue = calculateExposureValue(currentExposure);

      // Determine current zone
      let currentZone: 'safe' | 'warning' | 'critical';

      if (exposureValue <= riskRanges.criticalLow || exposureValue >= riskRanges.criticalHigh) {
        currentZone = 'critical';
      } else if (
        (exposureValue > riskRanges.criticalLow && exposureValue <= riskRanges.warningLow) ||
        (exposureValue >= riskRanges.warningHigh && exposureValue < riskRanges.criticalHigh)
      ) {
        currentZone = 'warning';
      } else {
        currentZone = 'safe';
      }

      // Count days in each zone
      if (currentZone === 'safe') {
        safeZoneDays += daysInCurrentState;

        // End of outside safe zone streak
        if (currentOutsideSafeStart) {
          const streakDays = getDaysDiff(currentOutsideSafeStart, currentDate);
          longestOutsideSafeZoneDays = Math.max(longestOutsideSafeZoneDays, streakDays);
          currentOutsideSafeStart = null;
        }
      } else {
        // Start tracking outside safe zone streak
        if (!currentOutsideSafeStart) {
          currentOutsideSafeStart = currentDate;
        }

        if (currentZone === 'warning') {
          totalWarningZoneDays += daysInCurrentState;
          // Count entries (when transitioning into warning zone)
          if (previousZone !== 'warning') {
            warningZoneEntries++;
          }
        } else if (currentZone === 'critical') {
          totalCriticalZoneDays += daysInCurrentState;
          // Count entries (when transitioning into critical zone)
          if (previousZone !== 'critical') {
            criticalZoneEntries++;
          }
        }
      }

      previousZone = currentZone;
    }

    // Handle case where we're still in an outside-safe-zone streak
    if (currentOutsideSafeStart) {
      const today = new Date();
      const streakDays = getDaysDiff(currentOutsideSafeStart, today);
      longestOutsideSafeZoneDays = Math.max(longestOutsideSafeZoneDays, streakDays);
    }

    // Calculate total days from first to last data point
    const firstDate = exposureHistory[0].createDate;
    const lastDate = exposureHistory[exposureHistory.length - 1].createDate;
    const totalDays = Math.max(1, getDaysDiff(firstDate, lastDate));

    const safeZonePercentage = totalDays > 0 ? Math.round((safeZoneDays / totalDays) * 100) : 0;

    return {
      safeZonePercentage,
      longestOutsideSafeZoneDays,
      warningZoneEntries,
      totalWarningZoneDays,
      criticalZoneEntries,
      totalCriticalZoneDays,
      totalDays,
    };
  }, [exposureHistory, riskRanges, calculateExposureValue]);

  const formatDuration = (days: number): string => {
    if (days === 0) return '0 days';
    if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;

    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    if (remainingDays === 0) {
      return `${weeks} week${weeks === 1 ? '' : 's'}`;
    }
    return `${weeks} week${weeks === 1 ? '' : 's'} and ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
  };

  return (
    <div className="flex flex-1 bg-white shadow-md flex-col overflow-y-auto">
      {/* Header Section */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Strategy Details
          </h3>
          <div className="flex items-center gap-2">
            {formulaId !== GENERAL_ID && formulaId && (
              <span className="text-xs text-gray-500">{formulaId}</span>
            )}
            {headerAction && (
              <MuiTooltip title={headerAction.isSaving ? 'Saving...' : 'Save allocations'} arrow>
                <span>
                  <button
                    onClick={headerAction.save}
                    disabled={!headerAction.canSave || headerAction.isSaving}
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                      headerAction.isSaving
                        ? 'bg-gray-100 text-gray-400'
                        : headerAction.canSave
                          ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    {headerAction.isSaving
                      ? <CircularProgress size={16} color="inherit" />
                      : <Save sx={{ fontSize: 18 }} />
                    }
                  </button>
                </span>
              </MuiTooltip>
            )}
          </div>
        </div>
      </div>

      {/* Hedges Deriving Section */}
      {activeSub === 'hedges-deriving' && accountId && (
        <div className="px-4 py-3 border-b border-gray-200">
          <HedgesDerivedMaterialsSection accountId={typeof accountId === 'string' ? parseInt(accountId) : accountId} />
        </div>
      )}

      {/* Unified Watch Rule Settings Panel(s) — show for tabs with rule type mapping */}
      {onWatchRuleSave && allWatchRules && allWatchRules.length > 0 && allRuleTypes ? (
        allWatchRules.map(rule => {
          const meta = allRuleTypes.find(rt => rt.ruleType === rule.rule_type);
          if (!meta) return null;
          return (
            <WatchRuleSettingsPanel
              key={rule.id}
              rule={rule}
              ruleMeta={meta}
              onSave={onWatchRuleSave}
              isSaving={isWatchRuleSaving}
              onCreateRule={onCreateRule ? () => onCreateRule(rule.rule_type) : undefined}
              isCreating={isCreatingRule}
              viewingFormulaId={formulaId !== GENERAL_ID && formulaId ? 1 : null}
              viewingFormulaName={accountMaterial?.displayName || formulaId}
            />
          );
        })
      ) : onWatchRuleSave && watchRuleMeta ? (
        <WatchRuleSettingsPanel
          rule={watchRule}
          ruleMeta={watchRuleMeta}
          onSave={onWatchRuleSave}
          isSaving={isWatchRuleSaving}
          onCreateRule={onCreateRule}
          isCreating={isCreatingRule}
          viewingFormulaId={formulaId !== GENERAL_ID && formulaId ? 1 : null}
          viewingFormulaName={accountMaterial?.displayName || formulaId}
        />
      ) : null}
      {activeSub === 'exposure' && (
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs text-gray-500 mb-2">
            Exposure is auto-calculated once a day.
            {latestSnapshot?.createdAt && (
              <> Last calculation: <span className="font-medium text-gray-700">{new Date(latestSnapshot.createdAt).toLocaleString()}</span></>
            )}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCalculateNow}
              disabled={isCalculating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCalculating
                ? <CircularProgress size={14} color="inherit" />
                : <Refresh sx={{ fontSize: 16 }} />
              }
              {isCalculating ? 'Calculating...' : 'Trigger fresh calculation'}
            </button>
            {calcResult && (
              <span className={`text-xs ${calcResult.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}>
                {calcResult}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Statistics Section - Only for exposure tab */}
      {activeSub === 'exposure' && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Exposure Statistics
            <span className="text-xs text-gray-500 font-normal ml-2">
              {toMonthStr(startDate)} - {toMonthStr(new Date())}
            </span>
          </h4>

        {exposureStats && exposureHistory && exposureHistory.length > 1 ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
              <span className="text-sm text-gray-700">
                Safe zone <strong className="text-gray-900">{exposureStats.safeZonePercentage}%</strong> of the time
              </span>
            </div>

            {exposureStats.longestOutsideSafeZoneDays > 0 && (
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 bg-yellow-600 rounded-full mt-1 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">
                  Longest period outside safe zone: <strong className="text-gray-900">{formatDuration(exposureStats.longestOutsideSafeZoneDays)}</strong>
                </span>
              </div>
            )}

            {exposureStats.warningZoneEntries > 0 && (
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 bg-yellow-600 rounded-full mt-1 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">
                  Warning zone entries: <strong className="text-gray-900">{exposureStats.warningZoneEntries}</strong> time{exposureStats.warningZoneEntries === 1 ? '' : 's'} ({formatDuration(exposureStats.totalWarningZoneDays)} total)
                </span>
              </div>
            )}

            {exposureStats.criticalZoneEntries > 0 && (
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">
                  Critical zone entries: <strong className="text-gray-900">{exposureStats.criticalZoneEntries}</strong> time{exposureStats.criticalZoneEntries === 1 ? '' : 's'} ({formatDuration(exposureStats.totalCriticalZoneDays)} total)
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No exposure data available for the selected period</p>
        )}
        </div>
      )}
    </div>
  );
};

